// signalR/notificationsConnection.js
// Conexión única de SignalR por pestaña, fuera del árbol de React.
// Ni los remontajes del layout ni los cambios de state.settings crean conexiones nuevas.
import * as signalR from "@microsoft/signalr";
import authService from "../components/api-authorization/AuthorizeService";

const LOG_PREFIX = "[SignalR]";
const RETRY_BASE_DELAY_MS = 2000;
const RETRY_MAX_DELAY_MS = 60000;

/** Error del accessTokenFactory cuando no hay un token vigente: el negotiate ni se envía. */
export class NoAccessTokenError extends Error {
    constructor() {
        super("Sin token de acceso vigente");
        this.name = "NoAccessTokenError";
    }
}

/**
 * Sin sesión válida: no hay token o el servidor respondió 401.
 * SignalR informa el 401 del negotiate dentro del mensaje ("... Status code '401'").
 */
export const isUnauthorized = error =>
    error?.name === "NoAccessTokenError" || /Status code '401'/.test(error?.message ?? "");

/**
 * Espera exponencial con jitter completo: valor aleatorio entre 0 y min(tope, base * 2^intento).
 * Nunca devuelve null: la reconexión no se rinde por cortes de red (ver retryPolicy para el 401).
 */
export const getRetryDelay = (retryCount, random = Math.random) => {
    const ceiling = Math.min(RETRY_MAX_DELAY_MS, RETRY_BASE_DELAY_MS * Math.pow(2, retryCount));
    return Math.floor(random() * ceiling);
};

const retryPolicy = {
    nextRetryDelayInMilliseconds: retryContext => {
        // Con la sesión vencida, reintentar solo produce 401: se corta y se espera un token nuevo
        if (isUnauthorized(retryContext.retryReason)) {
            waitForSession(retryContext.retryReason);
            return null;
        }
        const delay = getRetryDelay(retryContext.previousRetryCount);
        console.warn(`${LOG_PREFIX} Reconexión: intento ${retryContext.previousRetryCount + 1}, espera ${delay} ms`);
        return delay;
    }
};

let connection = null;
let hubUrl = null;
let startRetryTimer = null;
let startRetryCount = 0;
let listeners = [];
let waitingForSession = false;
let userLoadedSubscription = null;

const notify = () => {
    const snapshot = getSnapshot();
    listeners.forEach(listener => listener(snapshot));
};

const logState = (state, error) => {
    if (error)
        console.warn(`${LOG_PREFIX} Estado: ${state}`, error.message ?? error);
    else
        console.warn(`${LOG_PREFIX} Estado: ${state}`);
};

const clearStartRetry = () => {
    if (startRetryTimer) {
        clearTimeout(startRetryTimer);
        startRetryTimer = null;
    }
};

/**
 * Deja de reintentar hasta que oidc-client cargue un usuario nuevo (login o renovación del token).
 * Evita que una pestaña con la sesión vencida pida negotiate y reciba 401 indefinidamente.
 */
const waitForSession = error => {
    clearStartRetry();
    if (!waitingForSession)
        console.warn(`${LOG_PREFIX} Sin sesión válida: se pausa la conexión hasta renovar el token`, error?.message ?? error);
    waitingForSession = true;
    notify();
};

const resumeAfterSessionRenewed = () => {
    if (!waitingForSession || !connection) return;
    waitingForSession = false;
    startRetryCount = 0;
    if (connection.state === signalR.HubConnectionState.Disconnected)
        startWithRetry(connection);
};

/** start() con la misma política de reintentos que la reconexión automática (RF-005). */
const startWithRetry = conn => {
    logState(signalR.HubConnectionState.Connecting);

    const started = conn.start();
    // start() pasa el estado a Connecting en forma sincrónica: se notifica después para publicarlo
    notify();

    started
        .then(() => {
            if (conn !== connection) return;
            startRetryCount = 0;
            logState(conn.state);
            notify();
        })
        .catch(error => {
            if (conn !== connection) return;
            if (isUnauthorized(error))
                waitForSession(error);
            else
                scheduleStart(conn, error);
        });
};

const scheduleStart = (conn, error) => {
    const delay = getRetryDelay(startRetryCount);
    startRetryCount++;
    console.warn(`${LOG_PREFIX} Inicio fallido: intento ${startRetryCount}, espera ${delay} ms`, error?.message ?? error);
    notify();

    clearStartRetry();
    startRetryTimer = setTimeout(() => {
        startRetryTimer = null;
        if (conn === connection && conn.state === signalR.HubConnectionState.Disconnected)
            startWithRetry(conn);
    }, delay);
};

const buildConnection = url => {
    const conn = new signalR.HubConnectionBuilder()
        .withUrl(url, {
            // Token vigente en cada intento de conexión y reconexión
            accessTokenFactory: async () => {
                const token = await authService.getAccessToken();
                if (!token) throw new NoAccessTokenError();
                return token;
            }
        })
        .withAutomaticReconnect(retryPolicy)
        .configureLogging(signalR.LogLevel.Error)
        .build();

    // Keep-alive y timeout por defecto de SignalR (15 s / 30 s), coherentes con el servidor (RF-010)

    conn.onreconnecting(error => {
        if (conn !== connection) return;
        logState(signalR.HubConnectionState.Reconnecting, error);
        notify();
    });

    conn.onreconnected(() => {
        if (conn !== connection) return;
        logState(signalR.HubConnectionState.Connected);
        notify();
    });

    conn.onclose(error => {
        if (conn !== connection) return;
        logState(signalR.HubConnectionState.Disconnected, error);
        // La reconexión automática se corta ante un 401: se espera un token nuevo en vez de reintentar
        if (waitingForSession) return;
        if (isUnauthorized(error)) {
            waitForSession(error);
            return;
        }
        // Si la conexión se cierra por otro motivo, se reinicia con la misma política
        startRetryCount = 0;
        scheduleStart(conn, error);
    });

    return conn;
};

/**
 * Inicia la conexión. Es idempotente: si ya hay una conexión a la misma URL
 * (iniciada, iniciándose o esperando un reintento), no crea otra.
 */
export const start = notificationsApiUrl => {
    if (!notificationsApiUrl) return;

    const url = `${notificationsApiUrl}/notificationhub`;
    if (connection && hubUrl === url) return;

    if (connection) stop();

    hubUrl = url;
    startRetryCount = 0;
    waitingForSession = false;
    if (!userLoadedSubscription)
        userLoadedSubscription = authService.onUserLoaded(resumeAfterSessionRenewed);
    connection = buildConnection(url);
    startWithRetry(connection);
};

/** Detiene la conexión y cancela los reintentos pendientes. */
export const stop = () => {
    const conn = connection;
    clearStartRetry();
    connection = null;
    hubUrl = null;
    startRetryCount = 0;
    waitingForSession = false;
    if (userLoadedSubscription) {
        userLoadedSubscription.then(unsubscribe => unsubscribe()).catch(() => { });
        userLoadedSubscription = null;
    }

    if (conn) {
        logState(signalR.HubConnectionState.Disconnected);
        notify();
        return conn.stop().catch(() => { });
    }

    return Promise.resolve();
};

export const getConnection = () => connection;

export const getSnapshot = () => ({
    connection,
    state: connection ? connection.state : signalR.HubConnectionState.Disconnected,
    waitingForSession
});

/** Suscribe un listener a los cambios de estado. Devuelve la función para desuscribirse. */
export const onStateChange = listener => {
    listeners.push(listener);
    return () => {
        listeners = listeners.filter(l => l !== listener);
    };
};
